import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import type {
  FavoriteItem,
  FavoriteList,
  FavoriteItemType,
  FavoriteCachedData,
} from '@/types/favorites';

// Collection paths
const getUserFavoritesPath = (userId: string) => `artifacts/${appId}/users/${userId}/favorites`;
const getUserListsPath = (userId: string) => `artifacts/${appId}/users/${userId}/favoriteLists`;

/**
 * Remove undefined values from an object (Firestore doesn't allow undefined)
 * This handles nested objects as well
 */
function removeUndefinedValues<T extends object>(obj: T): Partial<T> {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date)
      ) {
        // For nested objects (except Date), recursively clean
        const nestedCleaned = removeUndefinedValues(value as Record<string, unknown>);
        if (Object.keys(nestedCleaned).length > 0) {
          cleaned[key] = nestedCleaned;
        }
      } else {
        cleaned[key] = value;
      }
    }
  }

  return cleaned as Partial<T>;
}

// ==================== Favorites CRUD ====================

/**
 * Add an item to user's favorites
 */
export async function addFavorite(
  userId: string,
  itemId: string,
  itemType: FavoriteItemType,
  cachedData: FavoriteCachedData
): Promise<string> {
  // Check if already favorited
  const existing = await findFavorite(userId, itemId, itemType);
  if (existing) {
    return existing.id;
  }

  const favoritesRef = collection(db, getUserFavoritesPath(userId));

  // Clean cachedData to remove undefined values (Firestore doesn't allow undefined)
  const cleanedCachedData = removeUndefinedValues(cachedData);

  const favoriteData = {
    itemId,
    itemType,
    listIds: [],
    createdAt: serverTimestamp(),
    cachedData: cleanedCachedData,
  };

  const docRef = await addDoc(favoritesRef, favoriteData);
  return docRef.id;
}

/**
 * Remove an item from user's favorites
 */
export async function removeFavorite(userId: string, favoriteId: string): Promise<void> {
  const favoriteRef = doc(db, getUserFavoritesPath(userId), favoriteId);
  await deleteDoc(favoriteRef);
}

/**
 * Remove a favorite by item ID and type (convenience method)
 */
export async function removeFavoriteByItem(
  userId: string,
  itemId: string,
  itemType: FavoriteItemType
): Promise<void> {
  const existing = await findFavorite(userId, itemId, itemType);
  if (existing) {
    await removeFavorite(userId, existing.id);
  }
}

/**
 * Find a favorite by item ID and type
 */
export async function findFavorite(
  userId: string,
  itemId: string,
  itemType: FavoriteItemType
): Promise<FavoriteItem | null> {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));
  const q = query(favoritesRef, where('itemId', '==', itemId), where('itemType', '==', itemType));

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as FavoriteItem;
}

/**
 * Check if an item is favorited
 */
export async function isFavorited(
  userId: string,
  itemId: string,
  itemType: FavoriteItemType
): Promise<boolean> {
  const favorite = await findFavorite(userId, itemId, itemType);
  return favorite !== null;
}

/**
 * Toggle favorite status for an item
 * Returns the new favorite if added, null if removed
 * Uses a transaction to prevent race conditions from concurrent toggles
 */
export async function toggleFavorite(
  userId: string,
  itemId: string,
  itemType: FavoriteItemType,
  cachedData: FavoriteCachedData
): Promise<{ favorited: boolean; favoriteId: string | null }> {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));

  // Query BEFORE the transaction - Firestore transactions don't support collection queries
  // Only transaction.get() for individual documents is supported inside transactions
  const q = query(favoritesRef, where('itemId', '==', itemId), where('itemType', '==', itemType));
  const snapshot = await getDocs(q);

  // Use a transaction to atomically verify and toggle favorite status
  // This prevents race conditions where concurrent toggles could create duplicates
  return await runTransaction(db, async (transaction) => {
    if (!snapshot.empty) {
      // Re-verify the document still exists inside the transaction
      const existingDocRef = snapshot.docs[0].ref;
      const existingDoc = await transaction.get(existingDocRef);

      if (existingDoc.exists()) {
        // Remove existing favorite
        transaction.delete(existingDocRef);
        return { favorited: false, favoriteId: null };
      }
      // Document was deleted between query and transaction - fall through to add
    }

    // Add new favorite (either no existing or was deleted)
    const newFavoriteRef = doc(favoritesRef);
    const cleanedCachedData = removeUndefinedValues(cachedData);

    transaction.set(newFavoriteRef, {
      itemId,
      itemType,
      listIds: [],
      createdAt: serverTimestamp(),
      cachedData: cleanedCachedData,
    });

    return { favorited: true, favoriteId: newFavoriteRef.id };
  });
}

/**
 * Get all favorites for a user
 */
export async function getFavorites(userId: string): Promise<FavoriteItem[]> {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));
  const q = query(favoritesRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as FavoriteItem
  );
}

/**
 * Get favorites by type
 */
export async function getFavoritesByType(
  userId: string,
  itemType: FavoriteItemType
): Promise<FavoriteItem[]> {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));
  const q = query(favoritesRef, where('itemType', '==', itemType), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as FavoriteItem
  );
}

/**
 * Get favorites count for a user
 */
export async function getFavoritesCount(userId: string): Promise<number> {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));
  const snapshot = await getDocs(favoritesRef);
  return snapshot.size;
}

// ==================== Custom Lists CRUD ====================

/**
 * Create a new custom list
 */
export async function createList(userId: string, name: string): Promise<string> {
  const listsRef = collection(db, getUserListsPath(userId));

  const listData = {
    name,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(listsRef, listData);
  return docRef.id;
}

/**
 * Update a custom list (rename)
 */
export async function updateList(userId: string, listId: string, name: string): Promise<void> {
  const listRef = doc(db, getUserListsPath(userId), listId);
  await updateDoc(listRef, {
    name,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a custom list
 * Also removes the list from all favorites that reference it
 */
export async function deleteList(userId: string, listId: string): Promise<void> {
  // Remove list reference from all favorites
  const favorites = await getFavorites(userId);
  const favoritesWithList = favorites.filter((f) => f.listIds.includes(listId));

  for (const favorite of favoritesWithList) {
    await removeFromList(userId, favorite.id, listId);
  }

  // Delete the list
  const listRef = doc(db, getUserListsPath(userId), listId);
  await deleteDoc(listRef);
}

/**
 * Get all custom lists for a user
 */
export async function getLists(userId: string): Promise<FavoriteList[]> {
  const listsRef = collection(db, getUserListsPath(userId));
  const q = query(listsRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as FavoriteList
  );
}

/**
 * Get a specific list by ID
 */
export async function getList(userId: string, listId: string): Promise<FavoriteList | null> {
  const listRef = doc(db, getUserListsPath(userId), listId);
  const snapshot = await getDoc(listRef);

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() } as FavoriteList;
}

// ==================== List Membership ====================

/**
 * Add a favorite to a custom list
 */
export async function addToList(userId: string, favoriteId: string, listId: string): Promise<void> {
  const favoriteRef = doc(db, getUserFavoritesPath(userId), favoriteId);
  await updateDoc(favoriteRef, {
    listIds: arrayUnion(listId),
  });

  // Update list's updatedAt timestamp
  const listRef = doc(db, getUserListsPath(userId), listId);
  await updateDoc(listRef, {
    updatedAt: serverTimestamp(),
  });
}

/**
 * Remove a favorite from a custom list
 */
export async function removeFromList(
  userId: string,
  favoriteId: string,
  listId: string
): Promise<void> {
  const favoriteRef = doc(db, getUserFavoritesPath(userId), favoriteId);
  await updateDoc(favoriteRef, {
    listIds: arrayRemove(listId),
  });
}

/**
 * Get favorites in a specific list
 */
export async function getFavoritesInList(userId: string, listId: string): Promise<FavoriteItem[]> {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));
  const q = query(
    favoritesRef,
    where('listIds', 'array-contains', listId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as FavoriteItem
  );
}

// ==================== Real-time Subscriptions ====================

/**
 * Subscribe to user's favorites (real-time updates)
 */
export function subscribeToFavorites(
  userId: string,
  callback: (favorites: FavoriteItem[]) => void
): () => void {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));
  const q = query(favoritesRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const favorites = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as FavoriteItem
    );
    callback(favorites);
  });
}

/**
 * Subscribe to user's custom lists (real-time updates)
 */
export function subscribeToLists(
  userId: string,
  callback: (lists: FavoriteList[]) => void
): () => void {
  const listsRef = collection(db, getUserListsPath(userId));
  const q = query(listsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const lists = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as FavoriteList
    );
    callback(lists);
  });
}

/**
 * Subscribe to favorites count (real-time updates)
 */
export function subscribeToFavoritesCount(
  userId: string,
  callback: (count: number) => void
): () => void {
  const favoritesRef = collection(db, getUserFavoritesPath(userId));

  return onSnapshot(favoritesRef, (snapshot) => {
    callback(snapshot.size);
  });
}

// ==================== Cleanup ====================

/**
 * Update cached data for a favorite (when source item is updated)
 */
export async function updateFavoriteCachedData(
  userId: string,
  favoriteId: string,
  cachedData: Partial<FavoriteCachedData>
): Promise<void> {
  const favoriteRef = doc(db, getUserFavoritesPath(userId), favoriteId);
  const snapshot = await getDoc(favoriteRef);

  if (!snapshot.exists()) return;

  const currentData = snapshot.data();
  // Clean cachedData to remove undefined values (Firestore doesn't allow undefined)
  const cleanedUpdate = removeUndefinedValues(cachedData);

  await updateDoc(favoriteRef, {
    cachedData: {
      ...currentData.cachedData,
      ...cleanedUpdate,
    },
  });
}

// ==================== Global Cleanup Functions ====================

/**
 * Remove all favorites that reference a specific item across all users
 * This is called when a library file or prayer is deleted
 *
 * Uses a collection group query to find all favorites with matching itemId
 * NOTE: Requires Firestore composite index on collection group 'favorites' with fields:
 *   - itemId (Ascending)
 *   - itemType (Ascending)
 */
export async function removeAllFavoritesForItem(
  itemId: string,
  itemType: FavoriteItemType
): Promise<number> {
  try {
    // Collection group query to find all favorites referencing this item
    const favoritesGroup = collectionGroup(db, 'favorites');
    const q = query(
      favoritesGroup,
      where('itemId', '==', itemId),
      where('itemType', '==', itemType)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return 0;
    }

    // Delete each matching favorite
    const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    return snapshot.size;
  } catch (error) {
    // Check if it's an index error and provide helpful message
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('index')) {
      console.error(
        `[Favorites Cleanup] MISSING FIRESTORE INDEX!\n` +
          `You need to create a composite index on collection group 'favorites'.\n` +
          `Click the link in the error below to create it:\n`,
        error
      );
    } else {
      console.error(
        `[Favorites Cleanup] Failed to remove favorites for ${itemType} ${itemId}:`,
        error
      );
    }
    // Don't throw - this is a cleanup operation that shouldn't block the main action
    return 0;
  }
}

/**
 * Update cached data for all favorites that reference a specific item across all users
 * This is called when a library file or prayer is updated
 *
 * Uses a collection group query to find all favorites with matching itemId
 * NOTE: Requires Firestore composite index on collection group 'favorites' with fields:
 *   - itemId (Ascending)
 *   - itemType (Ascending)
 */
/** Firestore allows at most 500 writes per batch */
const FAVORITES_BATCH_LIMIT = 500;

export async function updateAllFavoritesForItem(
  itemId: string,
  itemType: FavoriteItemType,
  cachedDataUpdates: Partial<FavoriteCachedData>
): Promise<number> {
  try {
    // Collection group query to find all favorites referencing this item
    const favoritesGroup = collectionGroup(db, 'favorites');
    const q = query(
      favoritesGroup,
      where('itemId', '==', itemId),
      where('itemType', '==', itemType)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return 0;
    }

    // Clean the updates to remove undefined values
    const cleanedUpdates = removeUndefinedValues(cachedDataUpdates);

    // Use writeBatch instead of parallel updateDoc calls (fewer round-trips). A Firestore batch
    // is limited to 500 operations, so a widely-favorited item must be committed in chunks.
    const docs = snapshot.docs;
    for (let i = 0; i < docs.length; i += FAVORITES_BATCH_LIMIT) {
      const batch = writeBatch(db);
      docs.slice(i, i + FAVORITES_BATCH_LIMIT).forEach((docSnap) => {
        const currentData = docSnap.data();
        batch.update(docSnap.ref, {
          cachedData: {
            ...currentData.cachedData,
            ...cleanedUpdates,
          },
        });
      });
      await batch.commit();
    }

    return snapshot.size;
  } catch (error) {
    // Check if it's an index error and provide helpful message
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('index')) {
      console.error(
        `[Favorites Sync] MISSING FIRESTORE INDEX!\n` +
          `You need to create a composite index on collection group 'favorites'.\n` +
          `Click the link in the error below to create it:\n`,
        error
      );
    } else {
      console.error(
        `[Favorites Sync] Failed to update favorites for ${itemType} ${itemId}:`,
        error
      );
    }
    // Don't throw - this is a sync operation that shouldn't block the main action
    return 0;
  }
}

/**
 * Mark favorites as orphaned when the source item no longer exists
 * This is an alternative to deletion - keeps the favorite but marks it as unavailable
 */
export async function markFavoritesAsOrphaned(
  itemId: string,
  itemType: FavoriteItemType
): Promise<number> {
  try {
    const favoritesGroup = collectionGroup(db, 'favorites');
    const q = query(
      favoritesGroup,
      where('itemId', '==', itemId),
      where('itemType', '==', itemType)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return 0;
    }

    const updatePromises = snapshot.docs.map(async (docSnap) => {
      const currentData = docSnap.data();
      await updateDoc(docSnap.ref, {
        cachedData: {
          ...currentData.cachedData,
          isDeleted: true,
          deletedAt: serverTimestamp(),
        },
      });
    });

    await Promise.all(updatePromises);

    return snapshot.size;
  } catch (error) {
    console.error(`Failed to mark favorites as orphaned for ${itemType} ${itemId}:`, error);
    return 0;
  }
}
